import React, { useEffect, useState } from 'react'
import AddEditForm from '../../../sharedComponents/AddEditForm'
import { PencilIcon } from '@heroicons/react/24/solid'
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import useManageCoupons from './useManageCoupons';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: '600px',
    maxHeight: '90vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

const emptyCoupon = {
  id: '',
  code: '',
  name: '',
  details: '',
  type: 'discount_amount',
  value: '',
  pointCost: '',
  maximumDiscountAmount: '',
  minimumSpend: '',
  isActive: true,
  isExchangeable: true,
};

const emptyGrant = {
  couponId: '',
  qty: '1',
  expiresInDays: '',
  targetType: 'user',
  identifier: '',
  tierId: '',
};

const inputClass =
  'px-3 py-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';

const CouponSetting = () => {
  const {
    coupons,
    tiers,
    grantHistory,
    refreshCoupons,
    createCoupon,
    editCoupon,
    deactivateCoupon,
    grantCoupon,
    searchCustomer,
    refreshGrantHistory,
    refreshTiers,
  } = useManageCoupons();

  // --- Coupon template create/edit ---
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [coupon, setCoupon] = useState(emptyCoupon);

  // --- Give modal ---
  const [isGiveOpen, setIsGiveOpen] = useState(false);
  const [grant, setGrant] = useState(emptyGrant);
  const [granting, setGranting] = useState(false);

  // --- Customer search ---
  const [searchTerm, setSearchTerm] = useState('');
  const [searched, setSearched] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [customerCoupons, setCustomerCoupons] = useState([]);

  const items = [
    { title: 'Code', name: 'code', type: 'text' },
    { title: 'Name', name: 'name', type: 'text' },
    { title: 'Details', name: 'details', type: 'textarea' },
    {
      title: 'Type',
      name: 'type',
      type: 'select',
      options: [
        { value: 'discount_amount', label: 'Discount amount (THB)' },
        { value: 'discount_percentage', label: 'Discount percentage (%)' },
      ],
    },
    { title: 'Value', name: 'value', type: 'number', step: '0.01' },
    { title: 'แลกด้วยคะแนนได้ (off = gift-only, admin gives it)', name: 'isExchangeable', type: 'boolean' },
    // Gift-only coupons cost 0 points — shown locked at 0 so it's clear nobody buys them.
    { title: 'Point Cost', name: 'pointCost', type: 'number', step: '0.1', disabled: !coupon.isExchangeable },
    { title: 'Max Discount Amount (percentage type only)', name: 'maximumDiscountAmount', type: 'number', step: '0.01' },
    { title: 'Minimum Spend (THB, blank = none)', name: 'minimumSpend', type: 'number', step: '0.01' },
    { title: 'Active', name: 'isActive', type: 'boolean' },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'isExchangeable') {
      // Off → points locked at 0; back on → blank, so a real cost must be typed.
      setCoupon((prev) => ({ ...prev, isExchangeable: checked, pointCost: checked ? '' : 0 }));
      return;
    }
    setCoupon((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const openCreateForm = () => {
    setCoupon(emptyCoupon);
    setFormMode('create');
    setIsFormOpen(true);
  };

  const openEditForm = (id) => {
    const target = coupons.find((c) => c.id === id);
    if (!target) return;
    setCoupon({
      id: target.id,
      code: target.code || '',
      name: target.name || '',
      details: target.details || '',
      type: target.type || 'discount_amount',
      value: target.value ?? '',
      pointCost: target.pointCost ?? '',
      maximumDiscountAmount: target.maximumDiscountAmount ?? '',
      minimumSpend: target.minimumSpend ?? '',
      isActive: !!target.isActive,
      isExchangeable: target.isExchangeable ?? true,
    });
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const closeForm = () => setIsFormOpen(false);

  const buildCouponPayload = () => ({
    code: coupon.code,
    name: coupon.name,
    details: coupon.details || null,
    type: coupon.type,
    value: coupon.value === '' ? undefined : Number(coupon.value),
    pointCost: coupon.pointCost === '' ? undefined : Number(coupon.pointCost),
    maximumDiscountAmount:
      coupon.maximumDiscountAmount === '' ? null : Number(coupon.maximumDiscountAmount),
    minimumSpend: coupon.minimumSpend === '' ? null : Number(coupon.minimumSpend),
    isActive: coupon.isActive,
    isExchangeable: coupon.isExchangeable,
  });

  const handleCreate = async () => {
    try {
      await createCoupon(buildCouponPayload());
      toast.success('Coupon created!');
      closeForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  const handleUpdate = async () => {
    try {
      await editCoupon(coupon.id, buildCouponPayload());
      toast.success('Coupon updated!');
      closeForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  const handleToggleActive = async (c) => {
    const activating = !c.isActive;
    if (!activating && !window.confirm('Deactivate this coupon? Users can no longer exchange for it.')) return;
    try {
      if (activating) {
        await editCoupon(c.id, { isActive: true });
        toast.success('Coupon activated.');
      } else {
        await deactivateCoupon(c.id);
        toast.success('Coupon deactivated.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  // --- Give modal ---
  const openGive = (prefill = {}) => {
    setGrant({ ...emptyGrant, ...prefill });
    setIsGiveOpen(true);
  };

  const closeGive = () => setIsGiveOpen(false);

  const handleGrantChange = (e) => {
    const { name, value } = e.target;
    setGrant((prev) => ({ ...prev, [name]: value }));
  };

  const submitGrant = async () => {
    if (!grant.couponId) {
      toast.error('Select a coupon to give.');
      return;
    }
    const target = { type: grant.targetType };
    if (grant.targetType === 'user') {
      if (!grant.identifier.trim()) {
        toast.error('Enter the user email, username, or social ID.');
        return;
      }
      target.identifier = grant.identifier.trim();
    } else if (grant.targetType === 'tier') {
      if (!grant.tierId) {
        toast.error('Select a tier.');
        return;
      }
      target.tierId = grant.tierId;
    } else if (grant.targetType === 'all') {
      if (!window.confirm('Give this coupon to ALL users? This cannot be undone.')) return;
    }

    setGranting(true);
    try {
      const res = await grantCoupon({
        couponId: grant.couponId,
        qty: Number(grant.qty) || 1,
        expiresInDays: grant.expiresInDays === '' ? undefined : Number(grant.expiresInDays),
        target,
      });
      toast.success(`Gave ${res.data.granted} coupon(s) to ${res.data.userCount} user(s).`);
      closeGive();
      refreshGrantHistory();
      if (searched && customer) runSearch(customer.email);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Give failed.');
    } finally {
      setGranting(false);
    }
  };

  // --- Customer search ---
  const runSearch = async (term) => {
    const value = (term ?? searchTerm).trim();
    if (!value) return;
    try {
      const { user, coupons: owned } = await searchCustomer(value);
      setCustomer(user);
      setCustomerCoupons(owned);
      setSearched(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Search failed.');
    }
  };

  const giveToThisCustomer = () => {
    if (!customer) return;
    openGive({ targetType: 'user', identifier: customer.email || customer.username || '' });
  };

  const targetLabel = (g) => {
    if (g.targetType === 'user') return g.targetUser ? (g.targetUser.nickname || g.targetUser.email) : 'User';
    if (g.targetType === 'tier') return g.tier ? `Tier: ${g.tier.name}` : 'Tier';
    return 'All users';
  };

  useEffect(() => {
    refreshCoupons();
    refreshTiers();
    refreshGrantHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {/* Coupon create/edit modal */}
      <Modal isOpen={isFormOpen} onRequestClose={closeForm} style={customStyles} ariaHideApp={false}>
        <AddEditForm
          items={items}
          name={formMode === 'edit' ? 'Update Coupon' : 'Create Coupon'}
          data={coupon}
          mode={formMode}
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          handleUpdate={handleUpdate}
          handleRegister={handleCreate}
          stateValue={coupon}
        />
      </Modal>

      {/* Give-for-free modal */}
      <Modal isOpen={isGiveOpen} onRequestClose={closeGive} style={customStyles} ariaHideApp={false}>
        <div className="p-2">
          <h2 className="text-center text-2xl font-bold text-gray-900 mb-6">Give coupon for free</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Coupon</label>
              <select name="couponId" value={grant.couponId} onChange={handleGrantChange} className={inputClass}>
                <option value="">Select a coupon</option>
                {coupons.filter((c) => c.isActive).map((c) => (
                  <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Quantity per user</label>
              <input type="number" min="1" name="qty" value={grant.qty} onChange={handleGrantChange} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Expires in (days, blank = 30)</label>
              <input type="number" min="1" name="expiresInDays" value={grant.expiresInDays} onChange={handleGrantChange} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Give to</label>
              <select name="targetType" value={grant.targetType} onChange={handleGrantChange} className={inputClass}>
                <option value="user">A single user</option>
                <option value="tier">All users in a tier</option>
                <option value="all">All users</option>
              </select>
            </div>
            {grant.targetType === 'user' && (
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">User email, username, or social ID</label>
                <input type="text" name="identifier" value={grant.identifier} onChange={handleGrantChange} className={inputClass} />
              </div>
            )}
            {grant.targetType === 'tier' && (
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">Tier</label>
                <select name="tierId" value={grant.tierId} onChange={handleGrantChange} className={inputClass}>
                  <option value="">Select a tier</option>
                  {tiers.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} ({t.tierNameTh})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex space-x-3 pt-2">
              <button
                onClick={submitGrant}
                disabled={granting}
                className="flex-1 rounded-md bg-green-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-800 disabled:opacity-50"
              >
                {granting ? 'Giving...' : 'Give coupon'}
              </button>
              <button
                onClick={closeGive}
                className="flex-1 rounded-md bg-gray-400 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <div className="px-4 sm:px-6 lg:px-8">
        {/* Section 1 — Coupon templates */}
        <div className="mt-4 flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-5xl">Coupon</h2>
            <div className="flex gap-3">
              <button
                onClick={() => openGive()}
                className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-800"
              >
                Give coupon
              </button>
              <button
                onClick={openCreateForm}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-800"
              >
                + Add Coupon
              </button>
            </div>
          </div>
          <div className="shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg overflow-hidden">
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(60vh)' }}>
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50 sticky top-0 z-0">
                  <tr>
                    <th className="py-4 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Code</th>
                    <th className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Name</th>
                    <th className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Type</th>
                    <th className="px-3 py-4 text-right text-sm font-semibold text-gray-900">Value</th>
                    <th className="px-3 py-4 text-right text-sm font-semibold text-gray-900">Point Cost</th>
                    <th className="px-3 py-4 text-center text-sm font-semibold text-gray-900">Exchange</th>
                    <th className="px-3 py-4 text-right text-sm font-semibold text-gray-900">Min Spend</th>
                    <th className="px-3 py-4 text-center text-sm font-semibold text-gray-900">Active</th>
                    <th className="relative py-4 pl-3 pr-4 sm:pr-6"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {coupons.map((c) => (
                    <tr key={c.id} className={c.isActive ? '' : 'bg-gray-50 text-gray-400'}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{c.code}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{c.name}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{c.type}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">{c.value}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">{c.pointCost}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                        {c.isExchangeable === false ? 'Gift only' : 'Points'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">{c.minimumSpend ?? '-'}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                        {c.isActive ? (
                          <span className="text-green-600 font-medium">Yes</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <PencilIcon
                          onClick={() => openEditForm(c.id)}
                          className="cursor-pointer inline-block w-6 h-6 fill-green-600 align-middle"
                        />
                        <button
                          onClick={() => handleToggleActive(c)}
                          className={`ml-3 rounded-md px-3 py-1 text-xs font-semibold text-white shadow-sm ${
                            c.isActive
                              ? 'bg-red-600 hover:bg-red-800'
                              : 'bg-green-600 hover:bg-green-800'
                          }`}
                        >
                          {c.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Section 2 — Customer coupons (search by customer) */}
        <div className="mt-10">
          <h3 className="text-2xl font-semibold mb-4">Customer coupons</h3>
          <div className="flex gap-3 mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              placeholder="Email, username, or social ID"
              className={`${inputClass} max-w-md`}
            />
            <button
              onClick={() => runSearch()}
              className="rounded-md bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-800"
            >
              Search
            </button>
          </div>

          {searched && !customer && (
            <p className="text-gray-500">No customer found for “{searchTerm}”.</p>
          )}

          {customer && (
            <div className="rounded-lg ring-1 ring-black ring-opacity-5 bg-white">
              <div className="flex justify-between items-center p-4 border-b border-gray-100">
                <div>
                  <div className="font-semibold text-gray-900">
                    {customer.nickname || `${customer.firstName} ${customer.lastName}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    {customer.email}
                    {customer.customerProfile?.socialId && <> · social: {customer.customerProfile.socialId}</>}
                  </div>
                </div>
                <button
                  onClick={giveToThisCustomer}
                  className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-800"
                >
                  Give to this customer
                </button>
              </div>
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Coupon</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Expires</th>
                    <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Used</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {customerCoupons.length === 0 && (
                    <tr><td colSpan={4} className="px-6 py-4 text-sm text-gray-500">This customer owns no coupons.</td></tr>
                  )}
                  {customerCoupons.map((uc) => (
                    <tr key={uc.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                        {uc.couponCode}
                        {uc.coupon && <div className="text-xs text-gray-400">{uc.coupon.type} · {uc.coupon.value}</div>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{uc.status}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {uc.expiresAt ? new Date(uc.expiresAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {uc.usedAt ? new Date(uc.usedAt).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Section 3 — Give history */}
        <div className="mt-10 mb-10">
          <h3 className="text-2xl font-semibold mb-4">Give history</h3>
          <div className="shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg overflow-hidden">
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(50vh)' }}>
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50 sticky top-0 z-0">
                  <tr>
                    <th className="py-4 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">When</th>
                    <th className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Coupon</th>
                    <th className="px-3 py-4 text-left text-sm font-semibold text-gray-900">Given to</th>
                    <th className="px-3 py-4 text-right text-sm font-semibold text-gray-900">Qty/user</th>
                    <th className="px-3 py-4 text-right text-sm font-semibold text-gray-900">Users</th>
                    <th className="px-3 py-4 text-left text-sm font-semibold text-gray-900">By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {grantHistory.length === 0 && (
                    <tr><td colSpan={6} className="px-6 py-4 text-sm text-gray-500">No coupons given yet.</td></tr>
                  )}
                  {grantHistory.map((g) => (
                    <tr key={g.id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-500 sm:pl-6">
                        {g.createdAt ? new Date(g.createdAt).toLocaleString() : '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                        {g.coupon ? g.coupon.code : `#${g.couponId}`}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{targetLabel(g)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">{g.qtyPerUser}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">{g.userCount}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {g.admin ? (g.admin.nickname || g.admin.username) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponSetting;
